import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { Row, Col } from 'react-bootstrap';
import Icon from '../../../../modules/react/components/stateless/Icon';
import currentPageContext from '../../../contexts/CurrentPageContext';
import Address from '../Address';
import FacilityLink from '../FacilityLink';
import FacilityName from '../FacilityName';
import FacilityImage from '../../../containers/FacilityImage';
import StarRack from '../StarRack';
import styles from './FacilitiesListEntry.scss';
import ApplicableFees from '../../stateful/ApplicableFees';

class FacilitiesListEntry extends React.Component {
  static propTypes = {
    facility: PropTypes.shape({
      address: PropTypes.shape({
        address1: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        country: PropTypes.string,
        fullAddress: PropTypes.string,
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        postal: PropTypes.string,
        state: PropTypes.string,
      }),
      currencySymbol: PropTypes.string,
      distanceFromCurrentUserInMiles: PropTypes.number,
      minimumUnitPrice: PropTypes.number,
      displayName: PropTypes.string.isRequired,
      phone: PropTypes.string,
      primaryImageMediumId: PropTypes.string,
      rating: PropTypes.number,
      settings: PropTypes.shape({
        businessAddressDescription: PropTypes.string,
        tagline: PropTypes.string,
      }),
      unitSummary: PropTypes.array.isRequired,
    }).isRequired,
    isEditable: PropTypes.bool,
    showLocation: PropTypes.bool,
  };

  static defaultProps = {
    showLocation: false,
    isEditable: undefined,
  };

  static contextType = currentPageContext;

  renderImage() {
    const { facility, isEditable } = this.props;

    return (
      <FacilityLink
        facility={facility}
        isEditable={isEditable}
      >
        <FacilityImage
          facility={facility}
          alt={facility.displayName}
          className={styles.picOfFacility}
        />
      </FacilityLink>
    );
  }

  renderPrice() {
    const { facility, isEditable } = this.props;
    const { secondaryColorBackgroundClass,
      secondaryColorBackgroundHoverClass,
      buttonStyleClass } = this.context;

    const feeFilter = {
      facilityId: facility?.id,
    };

    let priceTag = 'Call for Prices';
    if (typeof facility.minimumUnitPrice === 'number') {
      priceTag = `Prices start at ${facility.currencySymbol}${Math.round(facility.minimumUnitPrice)}`;
    }

    return (
      <>
        <Row className={styles.link}>
          <FacilityLink
            facility={facility}
            isEditable={isEditable}
          >
            <Col
              sm={3}
              className={`${styles.price}
            ${secondaryColorBackgroundClass}
            ${secondaryColorBackgroundHoverClass} ${buttonStyleClass}`}
            >
              <span className={styles.costAmt}>
                {priceTag}
              </span>
            </Col>
          </FacilityLink>
        </Row>
        <Row className={styles.applicableFeesLink}>
          <ApplicableFees
            facility={facility}
            feeFilter={feeFilter}
            displayApplicableFees={typeof facility.minimumUnitPrice === 'number'}
            feesDescriptionContainerClass={styles.appFeesDescriptionContainer}
          />
        </Row>
      </>
    );
  }


  renderGreyBar(className) {
    const { facility, showLocation } = this.props;
    const { secondaryColorClass } = this.context;

    if (!showLocation) {
      return false;
    }

    if (facility.distanceFromCurrentUserInMiles === 0) {
      facility.distanceFromCurrentUserInMiles = '< 1';
    }

    if (!facility.distanceFromCurrentUserInMiles) {
      return false;
    }

    return (
      <div className={className}>
        <div>
          <Icon
            className={`${styles.mapPin} ${secondaryColorClass}`}
            id="locations-pin"
          />
          {' '}
          {facility.distanceFromCurrentUserInMiles} miles
        </div>
        {facility.rating > 0 && (
          <StarRack className={styles.starRack} rating={facility.rating} />
        )}
      </div>
    );
  }

  renderAddressDescription = (facility) => {
    if (facility.settings && facility.settings.businessAddressDescription) {
      return (
        <div className={styles.addressDescription}>
          {facility.settings.businessAddressDescription}
        </div>
      );
    }
    return '';
  };

  renderTagline = (facility) => {
    if (facility.settings && facility.settings.tagline) {
      return (
        <div>{facility.settings.tagline}</div>
      );
    }
    return '';
  };

  render() {
    const { facility, isEditable, showLocation } = this.props;
    const { primaryColorClass, secondaryColorClass } = this.context;
    const phoneNumber = facility.phone || '';

    return (
      <div>
        <div className={styles.border}>
          <Row className={styles.rowStyle}>
            {showLocation
              && !Number.isNaN(facility.distanceFromCurrentUserInMiles)
              && (
                <Col xs={12}>
                  {this.renderGreyBar(styles.greyBar)}
                </Col>
              )
            }
            <Col md={3} sm={5} xs={12}>
              <div className={styles.imgWrap}>
                {this.renderImage()}
              </div>
            </Col>
            <Col md={9} sm={7} xs={12}>
              <div className={styles.nap}>
                <FacilityLink
                  facility={facility}
                  isEditable={isEditable}
                  className={`${styles.facilityName} ${primaryColorClass}`}
                >
                  <FacilityName {...facility} />
                </FacilityLink>
                {this.renderAddressDescription(facility)}
                <Address
                  {...facility.address}
                  className={styles.facilityAddress}
                  multiLine
                />
                <div className={`${styles.facilityPhone}`}>
                  <a href={`tel:${phoneNumber.replace(/[^\d]+/ig, '')}`} className={`${secondaryColorClass}`}>
                    {facility.phone}
                  </a>
                </div>
              </div>
            </Col>
          </Row>
        </div>
        <Row>
          <Col xs={12} sm={6} className={styles.tagline}>
            {this.renderTagline(facility)}
          </Col>
          <Col xs={12} sm={6}>
            {this.renderPrice()}
          </Col>
        </Row>
      </div>
    );
  }
}

FacilitiesListEntry.fragments = {
  facility: gql`
    fragment FacilitiesListEntryFacility on Facility {
      displayName
      address {
        address1
        address2
        city
        country
        fullAddress
        latitude
        longitude
        postal
        state
      }
      currencySymbol
      distanceFromCurrentUserInMiles
      minimumUnitPrice
      phone
      primaryImageMediumId
      applicableFeesEnabled
      settings {
        businessAddressDescription
        tagline
      }
      rating
      ...FacilityLinkFacility
      ...FacilityNameFacility
    }

    ${FacilityLink.fragments.facility}
    ${FacilityName.fragments.facility}
  `,
};

export default FacilitiesListEntry;
